import 'client-only'

import { IS_PROD_ENV, IS_PROD_BUILD, CONFIG_DISABLE_ATTENTIVE } from '@src/config/env'
import { initializeExperimentManager } from '@src/experiments'
import { statsigExperimentManager } from '@src/experiments/statsig-integration'
import { getIsNativeViewClient } from '@src/quince-site-common/src/helpers/getIsNativeViewClient'
import attachMessageListener from '@src/quince-site-common/src/helpers/window-message-listener'
import initializeAnalytics from '@src/quince-site-common/src/trackers/initializeAnalytics'
import addAttentiveSdk from '@src/quince-site-common/src/utils/addAttentiveSDK'
import shouldDisableTelemetry from '@src/quince-site-common/src/utils/check-qsf-telemetry'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import {
  executeSafely,
  extractQueryParamFromPageLocation,
  getUserSession,
  logoutUser,
  getCookie,
  saveState,
} from '@src/quince-site-integrated/src/utils'
import { UserAgnosticGlobalFeatureGates } from '@src/server-side/api/feature-gates.types'
import { shouldEnableI18nClient } from '@src/server-side/utils/i18n-preview.utils'
import TrustArcCl from '@src/telemetry/integrations/trustarc'
import { BLOCK_TELEMETRY_COOKIE_NAME, BLOCK_TELEMETRY_COOKIE_VALUE } from '@src/telemetry/telemetry-blockers.constants'
import { initializeConsoleMonkeyPatch } from '@src/utils/console-monkey-patch'
import { getContentVersionOnClient } from '@src/utils/general.utils'

declare global {
  interface Window {
    __debug: Record<string, unknown>
  }
}

function deleteTokensFromLocalStorage() {
  // eslint-disable-next-line @quince/no-direct-localstorage
  window.localStorage.removeItem('jwt_token')
}

const unregisterBlockedServiceWorkers = async () => {
  // eslint-disable-next-line i18next/no-literal-string
  const blockedWorkers = ['sw_v2.js']
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    for (const registration of registrations) {
      for (const worker of blockedWorkers) {
        if (registration.active?.scriptURL.endsWith(worker)) await registration.unregister()
      }
    }
  }
}

const saveFirstPurchaseCouponCode = saveState('firstPurchaseCouponCode')

function postInitialization() {
  console.log('CONFIG_DISABLE_ATTENTIVE :>> ', CONFIG_DISABLE_ATTENTIVE)
  attachMessageListener()
  if (!CONFIG_DISABLE_ATTENTIVE) {
    const { initializeAttentive } = addAttentiveSdk()
    initializeAttentive()
  }
  const discountCode = extractQueryParamFromPageLocation('discount_code')
  const isBase64Encoded = extractQueryParamFromPageLocation('is_enc')
  if (discountCode)
    saveFirstPurchaseCouponCode({
      code: isBase64Encoded ? atob(discountCode).toString() : discountCode,
    })

  unregisterBlockedServiceWorkers()
    .then(() => {
      // do nothing fire and forget
    })
    .catch((e: unknown) => {
      console.error('Error while unregistering blocked service workers', e)
    })
}

let isInitialized = false
const initializeClientEntryFunctions = () => {
  if (isInitialized || !isBrowser() || shouldDisableTelemetry()) {
    return
  }
  const blockTrackingCookie = getCookie(BLOCK_TELEMETRY_COOKIE_NAME) ?? ''
  const isPixelBlocked = blockTrackingCookie === BLOCK_TELEMETRY_COOKIE_VALUE.BLOCK_PIXEL
  const isIABlocked = blockTrackingCookie === BLOCK_TELEMETRY_COOKIE_VALUE.BLOCK_IA

  // Initialize TrustArc
  executeSafely(() => {
    TrustArcCl.init().catch((error: unknown) => {
      console.error('Error initializing TrustArc', error)
    })
  })

  // if (IS_PROD_ENV && IS_PROD_BUILD && !isPixelBlocked) {
  //   executeSafely(() => {
  //     initializeTelemetry()
  //   })
  // }

  // Initialize analytics
  executeSafely(() => {
    if (!isIABlocked)
      initializeAnalytics({
        enableTelemetry: IS_PROD_ENV && IS_PROD_BUILD && !isPixelBlocked,
      })
  })

  deleteTokensFromLocalStorage()
  executeSafely(() => {
    postInitialization()
  })
  isInitialized = true
}

const fetchInitialUserSessionForNativeView = () => {
  if (getIsNativeViewClient()) {
    getUserSession()
      .then(session => session)
      .catch((err: unknown) => {
        console.error('Error getting user session', err)
      })
  }
}

function initWindow() {
  if (!isBrowser()) return
  window.__debug = {
    release_build: process.env.NEXT_PUBLIC_RELEASE_VERSION,
    release_commit: process.env.NEXT_PUBLIC_RELEASE_COMMIT,
    isI18nEnabled: shouldEnableI18nClient(),
    previewDetails: {
      contentVersion: getContentVersionOnClient(),
    },
  }
  window.getUserSession = getUserSession
  window.logoutUser = logoutUser
  fetchInitialUserSessionForNativeView()
}

/**
 * This is intentional. You want auth to initialize before anything else
 * Reference - https://docs.amplify.aws/gen1/react/start/getting-started/setup/#set-up-frontend
 *
 * This function is intentionally added to run JS before any of
 * react components are mounted and effects are run.
 *
 * Please add only critical items here and get any changes reviewed by leads
 */
;(function initializeAtJSParse() {
  const isStatsigBlocked = (getCookie(BLOCK_TELEMETRY_COOKIE_NAME) ?? '') === BLOCK_TELEMETRY_COOKIE_VALUE.BLOCK_STATSIG

  // Initialize console monkey patch first (before any other logging)
  if (isBrowser()) {
    executeSafely(() => {
      initializeConsoleMonkeyPatch(IS_PROD_ENV && IS_PROD_BUILD)
    })
  }

  if (isBrowser()) {
    // Initialize experiment manager
    executeSafely(() => {
      initializeExperimentManager()
      if (!isStatsigBlocked) statsigExperimentManager.initialize()
    })
  }
})()

export function initializeClientEntry(_userAgnosticGlobalFeatureGates: UserAgnosticGlobalFeatureGates) {
  initializeClientEntryFunctions()
  initWindow()
}
